import { useRef, useState, useEffect } from 'react';

import { onMessage } from 'firebase/messaging';
import { messaging } from '../../../../firebase';

// material-ui
import { Grid } from '@mui/system';
import useMediaQuery from '@mui/material/useMediaQuery';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { toast } from 'react-toastify';
import fetchWithRefresh from '../../../../utils/fetchWithRefresh';
import { parse, format } from 'date-fns';

// project imports
import MainCard from '../../../../components/MainCard';
import IconButton from '../../../../components/@extended/IconButton';
import Transitions from '../../../../components/@extended/Transitions';

// assets
import { AiOutlineBell, AiOutlineWarning } from 'react-icons/ai';
import { useUserContext } from '../../../../contexts/UserContext';
import { useNavigate } from 'react-router-dom';

// sx styles
const avatarSX = {
  width: 30,
  height: 30,
  fontSize: '16px'
};

const actionSX = {
  mt: '6px',
  ml: 1,
  top: 'auto',
  right: 'auto',
  alignSelf: 'flex-start',

  transform: 'none'
};

// ==============================|| HEADER CONTENT - NOTIFICATION ||============================== //

export default function Notification() {
  const downMD = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const { user, logout, updateTokens, selectedVenueData } = useUserContext();
  const [notifications, setNotifications] = useState([]);

  const fetchNotifications = async () => {
    try {
      const response = await fetchWithRefresh(
        `${import.meta.env.VITE_API_URL}Incident/FetchActiveIncidentsForVenue?venueId=${selectedVenueData?.orgID}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.token}`,
          },
        },
        logout,
        updateTokens,
        (message) => {
          toast.info(message);
        }
      )

      if (response.ok) {
        const data = await response.json();
        setNotifications(data);
        console.log(data);
      } else {
        throw new Error("Failed to fetch incidents");
      }
    } catch (error) {
      console.log(error)
    }
  };

  useEffect(() => {
    if(!selectedVenueData?.orgID) return;

    fetchNotifications();

    const unsubscribe = onMessage(messaging, async (payload) => {
      // optionally store raw payload if you want
      // setActiveIncident(payload);

      // re-fetch notifications so UI updates
      await fetchNotifications(); 
    });
    
    return () => unsubscribe();
  }, [selectedVenueData?.orgID]);

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <IconButton
        color="secondary"
        variant="light"
        sx={(theme) => ({
          color: 'text.primary',
          bgcolor: notifications.length > 0 ? 'error.main' : 'transparent',
          '&:hover': {
            bgcolor: notifications.length > 0 ? 'error.main' : 'transparent',
            color: 'text.primary',
          },
          ...theme.applyStyles('dark', { bgcolor: open ? 'background.default' : 'transparent' })
        })}
        aria-label="open profile"
        ref={anchorRef}
        aria-controls={open ? 'profile-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <Badge>
          <AiOutlineBell style={{ fill: notifications.length > 0 ? 'white' : 'black' }} />
        </Badge>
      </IconButton>
      <Popper
        placement={downMD ? 'bottom' : 'bottom-end'}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{ modifiers: [{ name: 'offset', options: { offset: [downMD ? -5 : 0, 9] } }] }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position={downMD ? 'top' : 'top-right'} in={open} {...TransitionProps}>
            <Paper sx={(theme) => ({ boxShadow: theme.customShadows.z1, width: '100%', minWidth: 285, maxWidth: { xs: 285, md: 420 } })}>
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard
                  title="Active Incidents"
                  elevation={0}
                  border={false}
                  content={false}
                >
                  <List
                    component="nav"
                    sx={{
                      p: 0,
                      '& .MuiListItemButton-root': {
                        py: 0.5,
                        px: 2,
                        '&.Mui-selected': { bgcolor: 'grey.50', color: 'text.primary' },
                        '& .MuiAvatar-root': avatarSX,
                        '& .MuiListItemSecondaryAction-root': { ...actionSX, position: 'relative' }
                      }
                    }}
                  >
                    {notifications.length > 0 ? (
                      notifications.map((n) => (
                      <ListItem 
                        key={n.id} 
                        component={ListItemButton}
                        divider
                        onClick={() => {
                          navigate(`/dashboard/venue/${selectedVenueData.orgID}/manage/incidents/view/${n.EmergencyId}`);
                          setOpen(false);
                        }}
                        secondaryAction={
                          <Grid container direction="column" alignItems={'flex-end'} justifyContent={'flex-end'} textAlign={'right'}>
                            <Typography variant="caption">
                              {format(parse(n?.WhenOpened, 'yyyy-MM-dd HH:mm:ss', new Date()), 'dd/MM/yyyy')}
                            </Typography>

                            <Typography variant="caption">
                              {format(parse(n?.WhenOpened, 'yyyy-MM-dd HH:mm:ss', new Date()), 'HH:mm')}
                            </Typography>
                          </Grid>
                        }
                      >
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: 'error.main', width: 20, height: 20 }}><AiOutlineWarning /></Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={<Typography variant="h6">{n.ParticipantName}</Typography>}
                          secondary={n.ZoneName}
                        />
                      </ListItem>
                      ))
                    ) : (
                      <Box sx={{py: 0.5,
                        px: 2,}}>
                        <Typography variant="caption">
                          There are no active incidents at your current venue.
                        </Typography>
                      </Box>
                    )}

                    <ListItemButton
                      component="a"
                      to={`/dashboard/venue/${selectedVenueData.orgID}/manage/incidents`}
                      sx={{ textAlign: 'center', py: '12px !important' }}
                    >
                      <ListItemText
                        primary={
                          <Typography variant="h6" color="primary">
                            View All Incidents
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  </List>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
}
